import { useEffect, useState } from "react";
import type { DuplicateCandidate, PatientFileWithGlance, SexName } from "@dentacore/shared-types";
import { PHONE_ALREADY_USED } from "@dentacore/shared-types";

import { ApiError } from "../api/client";
import {
  erasePatient,
  getPatient,
  possibleDuplicates,
  removePatient,
  updatePatient,
} from "../api/patients";
import { useAuth } from "../auth/auth-context";
import { MergeModal } from "./duplicates";
import { ConfirmDialog } from "../ui/confirm-dialog";
import { PhoneInput } from "../ui/phone-input";
import { Modal } from "../ui/modal";
import { InsurerField } from "./insurer-field";
import styles from "./edit-patient-modal.module.css";
import { RequiredMark } from "../ui/required-mark";

/**
 * Editing a patient's own details — name, phone, insurer and the rest.
 *
 * **The mockup has no equivalent screen for this.** It only ever creates a
 * file; every one of its eleven patients is already correct. This modal is
 * therefore this screen's own design, following the same bordered-card
 * pattern the Recent-visits and Imaging modals already use, rather than
 * reusing a mockup layout that does not exist.
 *
 * **Date of birth and recall date can be cleared** (QF-D37) — emptying either
 * input, or its *Clear*, sends a null, which the API now stores as no date.
 * It used to store 1 January 1970, which is why this form once refused to.
 * A date that was never set and is still empty is not sent at all.
 */
export function EditPatientModal({
  file,
  onSaved,
  onClose,
  onRemoved,
}: {
  file: PatientFileWithGlance;
  onSaved: (file: PatientFileWithGlance) => void;
  onClose: () => void;
  /**
   * Removing the file leaves nothing to show, so the caller navigates away
   * (QA #38). Absent for a non-admin, which is what hides the control.
   */
  onRemoved?: () => void;
}): React.JSX.Element {
  const [firstName, setFirstName] = useState(file.firstName);
  const [lastName, setLastName] = useState(file.lastName);
  const [dateOfBirth, setDateOfBirth] = useState(file.dateOfBirth ?? "");
  const [sex, setSex] = useState<SexName | "">(file.sex ?? "");
  const [phone, setPhone] = useState(file.phoneE164);
  const [email, setEmail] = useState(file.email ?? "");
  const [address, setAddress] = useState(file.address ?? "");
  const [insurer, setInsurer] = useState(file.insurerName ?? "");
  const [policyReference, setPolicyReference] = useState(file.policyReference ?? "");
  const [recallDueOn, setRecallDueOn] = useState(file.recallDueOn ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * STORY-122 (SA3). Holds the saved file while a collision warning is on
   * screen — the save already happened, so `onSaved` fires the moment this is
   * acknowledged, never before. No collision means this is skipped entirely
   * and `onSaved` fires immediately, exactly as it always did.
   */
  const [savedPendingAck, setSavedPendingAck] = useState<PatientFileWithGlance | null>(null);
  /** Another file already has the number (audit F-13) — the same confirm as a new file. */
  const [clash, setClash] = useState<{
    patientName: string | null;
    fileNumber: string | null;
  } | null>(null);

  /**
   * **Merge from a file** (PP-D13): an administrator sees files that may be
   * this person and can merge one into it, after the same preview the list's
   * merge shows.
   */
  const administrator = useAuth().user?.canAdminister === true;
  const [lookalikes, setLookalikes] = useState<readonly DuplicateCandidate[]>([]);
  const [merging, setMerging] = useState<string | null>(null);
  useEffect(() => {
    if (!administrator || file.dateOfBirth === null) return;
    const controller = new AbortController();
    possibleDuplicates(
      {
        firstName: file.firstName,
        lastName: file.lastName,
        dateOfBirth: file.dateOfBirth,
        excludeId: file.id,
      },
      controller.signal,
    )
      .then(setLookalikes)
      .catch(() => {
        /* Nothing offered. */
      });
    return () => controller.abort();
  }, [administrator, file]);

  const canSave = firstName.trim() !== "" && lastName.trim() !== "" && phone.trim() !== "" && !busy;

  const save = (shared = false): void => {
    if (!canSave) return;

    setBusy(true);
    setError(null);

    updatePatient(file.id, {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneE164: phone.trim(),
      // A blank date clears a stored one, and sends nothing when there was none.
      ...dateChange("dateOfBirth", dateOfBirth, file.dateOfBirth),
      ...dateChange("recallDueOn", recallDueOn, file.recallDueOn),
      sex: sex === "" ? null : sex,
      email: email.trim() === "" ? null : email.trim(),
      address: address.trim() === "" ? null : address.trim(),
      insurerName: insurer.trim() === "" ? null : insurer.trim(),
      policyReference: policyReference.trim() === "" ? null : policyReference.trim(),
      ...(shared ? { sharesHouseholdNumber: true } : {}),
      // **What this form opened with** (PP-D12): a field someone else changed
      // since is refused by name, and a field left alone is not written back.
      expected: {
        firstName: file.firstName,
        lastName: file.lastName,
        phoneE164: file.phoneE164,
        dateOfBirth: file.dateOfBirth,
        recallDueOn: file.recallDueOn,
        sex: file.sex,
        email: file.email,
        address: file.address,
        insurerName: file.insurerName,
        policyReference: file.policyReference,
      },
    })
      .then((saved) => {
        setClash(null);
        if (saved.staffCollision == null && saved.phoneWarning == null) onSaved(saved);
        else setSavedPendingAck(saved);
      })
      .catch((cause: unknown) => {
        if (cause instanceof ApiError && cause.code === PHONE_ALREADY_USED) {
          const details = cause.details ?? {};
          const name = details["patientName"];
          const number = details["fileNumber"];
          setClash({
            patientName: typeof name === "string" ? name : null,
            fileNumber: typeof number === "string" ? number : null,
          });
          return;
        }
        setError(cause instanceof Error ? cause.message : "Could not save these changes.");
      })
      .finally(() => setBusy(false));
  };

  if (merging !== null) {
    return (
      <MergeModal
        keepId={file.id}
        otherId={merging}
        onClose={() => setMerging(null)}
        onMerged={(keptId) => {
          getPatient(keptId)
            .then(onSaved)
            .catch(() => onClose());
        }}
      />
    );
  }

  if (savedPendingAck !== null) {
    const collision = savedPendingAck.staffCollision;
    return (
      <Modal label="Edit patient details" testId="edit-patient-modal" onClose={onClose}>
        <h2 className={styles.title}>Saved</h2>
        {collision != null && (
          <p data-testid="edit-patient-staff-collision">
            This number is also {collision.name}&rsquo;s WhatsApp assistant number. Worth checking
            it belongs to the right person.
          </p>
        )}
        {/* Saved, with a warning (PP-D15): the right length, not a number for its country. */}
        {savedPendingAck.phoneWarning != null && (
          <p data-testid="edit-patient-phone-warning">{savedPendingAck.phoneWarning}</p>
        )}
        <button
          type="button"
          className={styles.primary}
          onClick={() => onSaved(savedPendingAck)}
          data-testid="edit-patient-staff-collision-ack"
        >
          Continue
        </button>
      </Modal>
    );
  }

  return (
    <Modal label="Edit patient details" testId="edit-patient-modal" onClose={onClose}>
      <h2 className={styles.title}>Edit patient details</h2>

      {error !== null && (
        <p className={styles.error} data-testid="edit-patient-error">
          {error}
        </p>
      )}

      {lookalikes.length > 0 && (
        <div className={styles.lookalikes} data-testid="edit-patient-lookalikes">
          {lookalikes.map((other) => (
            <p key={other.id}>
              {other.name} #{other.fileNumber} has the same date of birth.{" "}
              <button
                type="button"
                className={styles.clear}
                onClick={() => setMerging(other.id)}
                data-testid="edit-patient-merge"
              >
                Merge into this file…
              </button>
            </p>
          ))}
        </div>
      )}

      <div className={styles.fields}>
        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>
              First name
              <RequiredMark />
            </span>
            <input
              className={styles.input}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              data-testid="edit-patient-first-name"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>
              Last name
              <RequiredMark />
            </span>
            <input
              className={styles.input}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              data-testid="edit-patient-last-name"
            />
          </label>
        </div>

        <div className={styles.row}>
          <div className={styles.field}>
            <label>
              <span className={styles.label}>Date of birth</span>
              <input
                className={styles.input}
                type="date"
                value={dateOfBirth.slice(0, 10)}
                onChange={(event) => setDateOfBirth(event.target.value)}
                data-testid="edit-patient-dob"
              />
            </label>
            {dateOfBirth !== "" && (
              <button
                type="button"
                className={styles.clear}
                onClick={() => setDateOfBirth("")}
                data-testid="edit-patient-dob-clear"
              >
                Clear date of birth
              </button>
            )}
          </div>
          <label className={styles.field}>
            <span className={styles.label}>Sex</span>
            <select
              className={styles.input}
              value={sex}
              onChange={(event) => setSex(event.target.value as SexName | "")}
              data-testid="edit-patient-sex"
            >
              <option value="">Not recorded</option>
              <option value="MALE">Male</option>
              <option value="FEMALE">Female</option>
            </select>
          </label>
        </div>

        <PhoneInput value={phone} onChange={setPhone} testIdPrefix="edit-patient-phone" required />

        <div className={styles.row}>
          <label className={styles.field}>
            <span className={styles.label}>Email</span>
            <input
              className={styles.input}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              data-testid="edit-patient-email"
            />
          </label>
          <label className={styles.field}>
            <span className={styles.label}>Address</span>
            <input
              className={styles.input}
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              data-testid="edit-patient-address"
            />
          </label>
        </div>

        <div className={styles.row}>
          <InsurerField
            value={insurer}
            onChange={setInsurer}
            testId="edit-patient-insurer"
            className={styles.field}
          />
          <label className={styles.field}>
            <span className={styles.label}>Policy reference</span>
            <input
              className={styles.input}
              value={policyReference}
              onChange={(event) => setPolicyReference(event.target.value)}
              data-testid="edit-patient-policy"
            />
          </label>
        </div>

        <div className={styles.field}>
          <label>
            <span className={styles.label}>Recall due</span>
            <input
              className={styles.input}
              type="date"
              value={recallDueOn.slice(0, 10)}
              onChange={(event) => setRecallDueOn(event.target.value)}
              data-testid="edit-patient-recall"
            />
          </label>
          {recallDueOn !== "" && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => setRecallDueOn("")}
              data-testid="edit-patient-recall-clear"
            >
              Clear recall date
            </button>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary}
          disabled={!canSave}
          onClick={() => save()}
          data-testid="edit-patient-save"
        >
          Save changes
        </button>
        <button
          type="button"
          className={styles.secondary}
          disabled={busy}
          onClick={onClose}
          data-testid="edit-patient-cancel"
        >
          Cancel
        </button>

        {/*
            QA #38 — `DELETE /patients/:id` has worked since STORY-017 and no
            screen called it, so a file opened by mistake stayed for good.

            Admin only, because `onRemoved` is only passed for an admin: the
            server enforces it (P2 Q3) and this is the affordance, not the rule.
            Confirmed, and the confirmation says the removal can be undone —
            which is true, and is what makes this a safe thing to offer at all.
          */}
        {onRemoved !== undefined && (
          <button
            type="button"
            className={styles.destructive}
            disabled={busy}
            onClick={() => {
              const sure = window.confirm(
                `Remove ${file.firstName} ${file.lastName}'s file (${file.fileNumberDisplay})?\n\n` +
                  `Nothing clinical is destroyed — an admin can put it back from ` +
                  `Patients → Removed files.`,
              );
              if (!sure) return;

              setBusy(true);
              setError(null);
              removePatient(file.id)
                .then(onRemoved)
                .catch((cause: unknown) => {
                  setError(cause instanceof Error ? cause.message : "Could not remove this file.");
                  setBusy(false);
                });
            }}
            data-testid="edit-patient-remove"
          >
            Remove file
          </button>
        )}

        {/*
          **Erase identity** (PP-D16, ER-D1): a patient who asks to be forgotten.
          Administrators only, confirmed by a sentence naming what goes and
          what stays, because it cannot be undone.
        */}
        {administrator && (
          <button
            type="button"
            className={styles.destructive}
            disabled={busy}
            onClick={() => {
              const sure = window.confirm(
                `Erase ${file.firstName} ${file.lastName}'s identity?\n\n` +
                  "Goes: name, phone, email, address, insurer, policy and contacts, " +
                  "and from WhatsApp the number and every message's words.\n" +
                  "Stays: the chart, notes, images, plans and invoices, under " +
                  `"Erased patient ${file.fileNumberDisplay}".\n\nThis cannot be undone.`,
              );
              if (!sure) return;
              setBusy(true);
              setError(null);
              erasePatient(file.id)
                .then(onSaved)
                .catch((cause: unknown) => {
                  setError(
                    cause instanceof Error ? cause.message : "Could not erase this identity.",
                  );
                  setBusy(false);
                });
            }}
            data-testid="edit-patient-erase"
          >
            Erase identity
          </button>
        )}
      </div>

      {clash !== null && (
        <ConfirmDialog
          title="That number is already on a file"
          confirmLabel="Save anyway"
          busy={busy}
          onCancel={() => setClash(null)}
          onConfirm={() => save(true)}
          testId="edit-patient-share-number"
        >
          <p>
            {clash.patientName === null
              ? "Another patient already has this number."
              : `${clash.patientName}${
                  clash.fileNumber === null ? "" : ` (file ${clash.fileNumber})`
                } already has this number.`}
          </p>
          <p>
            Save anyway if two people genuinely share it &mdash; a couple, or a parent and child.
          </p>
        </ConfirmDialog>
      )}
    </Modal>
  );
}

/** A date field's part of the PATCH: the new date, a null to clear, or nothing. */
function dateChange<K extends "dateOfBirth" | "recallDueOn">(
  key: K,
  typed: string,
  stored: string | null,
): Partial<Record<K, string | null>> {
  if (typed !== "") return { [key]: typed } as Partial<Record<K, string | null>>;
  return stored === null ? {} : ({ [key]: null } as Partial<Record<K, string | null>>);
}
